import React, { useCallback, useEffect, useState } from 'react';
import { getPersoneller, getShiftHafta, shiftHaftalikOlustur, shiftGunDegistir, shiftGunSil, shiftHaftaTemizle, getShiftSecenekleri } from '../services/api';
import { haftaBaslangici, toISODate, tarihTR, gunAdiKisa, haftalarByAy, ayYYYYMM, haftaGunleri, haftaEtiket } from '../utils/hafta';

// Toplu shift planı geçmiş haftalar için oluşturulamaz; bu hafta veya sonrası için oluşturulabilir
// (backend'de de aynı kural var). Bu haftadaki geçmiş günler otomatik atlanır, yalnızca bugün ve sonrası için kayıt açılır.
const buHafta = haftaBaslangici(new Date());
const buHaftaISO = toISODate(buHafta);

// Matris hücrelerinde kısa gösterim (saat:dakika yerine sadece saat).
const TUR_ETIKET_KISA = {
  SHIFT_10_20: '10–20',
  SHIFT_11_21: '11–21',
  SHIFT_12_22: '12–22',
  PAZAR_10_16: '10–16',
};

const TUR_ETIKET = {
  SHIFT_10_20: '10:00–20:00',
  SHIFT_11_21: '11:00–21:00',
  SHIFT_12_22: '12:00–22:00',
  PAZAR_10_16: '10:00–16:00 (Pazar)',
};

const TUR_BADGE = {
  SHIFT_10_20: 'badge-shift10',
  SHIFT_11_21: 'badge-shift11',
  SHIFT_12_22: 'badge-shift12',
  PAZAR_10_16: 'badge-pazar',
};

// PART_TIME kaydının saatleri sabit değil, o yüzden matris/modalde kayıttaki gerçek saatlerden üretilir.
const kisaEtiket = (kayit) =>
  kayit.shiftTuru === 'PART_TIME' ? `${kayit.baslangicSaat}–${kayit.bitisSaat}` : (TUR_ETIKET_KISA[kayit.shiftTuru] || kayit.shiftTuru);
const uzunEtiket = (kayit) =>
  kayit.shiftTuru === 'PART_TIME' ? `${kayit.baslangicSaat}–${kayit.bitisSaat} (Part Time)` : (TUR_ETIKET[kayit.shiftTuru] || kayit.shiftTuru);
const badgeSinifi = (kayit) => (kayit.shiftTuru === 'PART_TIME' ? 'badge-parttime' : (TUR_BADGE[kayit.shiftTuru] || ''));

// Part time personel için pratik saat dropdown'ı: 06:00–23:30 arası 30 dakikalık adımlar.
const SAAT_SECENEKLERI = (() => {
  const liste = [];
  for (let dk = 6 * 60; dk <= 23 * 60 + 30; dk += 30) {
    const s = String(Math.floor(dk / 60)).padStart(2, '0');
    const d = String(dk % 60).padStart(2, '0');
    liste.push(`${s}:${d}`);
  }
  return liste;
})();

export default function ShiftYonetimi() {
  const [ayGirdi, setAyGirdi] = useState(ayYYYYMM(buHafta));
  const [seciliHaftaISO, setSeciliHaftaISO] = useState(buHaftaISO);
  const [personelFiltre, setPersonelFiltre] = useState('');
  const [personeller, setPersoneller] = useState([]);
  const [kayitlar, setKayitlar] = useState([]);
  const [hata, setHata] = useState('');
  const [basari, setBasari] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [topluOlusturuluyor, setTopluOlusturuluyor] = useState(false);
  const [temizleniyor, setTemizleniyor] = useState(false);

  // modal: { dokKod, personelAdi, partTime, tarihISO, kayit, adim: 'secim'|'duzenle', baslangicSaat, bitisSaat, kaydediliyor, secenekler }
  const [modal, setModal] = useState(null);

  const haftalar = haftalarByAy(ayGirdi);
  const hafta = haftaBaslangici(seciliHaftaISO);
  const haftaBitis = new Date(hafta.getTime() + 6 * 86400000);
  const olusturmaIzinli = seciliHaftaISO >= buHaftaISO;
  const gunler = haftaGunleri(hafta);

  const gosterilenPersoneller = personelFiltre
    ? personeller.filter(p => String(p.kod) === String(personelFiltre))
    : personeller;

  // dokKod -> tarih(ISO) -> kayit şeklinde hızlı erişim için tablo.
  const matris = {};
  for (const k of kayitlar) {
    const gISO = toISODate(new Date(k.shiftTarihi));
    if (!matris[k.dokKod]) matris[k.dokKod] = {};
    matris[k.dokKod][gISO] = k;
  }

  useEffect(() => {
    getPersoneller().then(res => setPersoneller(res.data)).catch(() => {});
  }, []);

  const listeYukle = useCallback(() => {
    setYukleniyor(true);
    getShiftHafta(seciliHaftaISO)
      .then(res => setKayitlar(res.data))
      .catch(() => setHata('Shift kayıtları yüklenemedi.'))
      .finally(() => setYukleniyor(false));
  }, [seciliHaftaISO]);

  useEffect(() => { listeYukle(); }, [listeYukle]);

  const topluOlustur = async () => {
    if (!window.confirm(`${tarihTR(hafta)} – ${tarihTR(haftaBitis)} haftası için tüm aktif personele shift planı oluşturulsun mu?`)) return;
    setHata(''); setBasari('');
    setTopluOlusturuluyor(true);
    try {
      const res = await shiftHaftalikOlustur(seciliHaftaISO);
      setBasari(`${res.data.olusturulanSayisi} shift kaydı oluşturuldu.`);
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Toplu shift planı oluşturulamadı.');
    } finally {
      setTopluOlusturuluyor(false);
    }
  };

  const haftayiTemizle = async () => {
    if (!window.confirm(`${tarihTR(hafta)} – ${tarihTR(haftaBitis)} haftasının TÜM shift kayıtları silinsin mi? Bu işlem geri alınamaz.`)) return;
    setHata(''); setBasari('');
    setTemizleniyor(true);
    try {
      const res = await shiftHaftaTemizle(seciliHaftaISO);
      setBasari(`${res.data.silinenSayisi} shift kaydı silindi.`);
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Hafta temizlenemedi.');
    } finally {
      setTemizleniyor(false);
    }
  };

  const hucreyeTikla = (dokKod, personelAdi, tarihISO, kayit) => {
    setHata(''); setBasari('');
    const partTime = personeller.find(p => p.kod === dokKod)?.partTime || 0;
    if (kayit) {
      setModal({ dokKod, personelAdi, partTime, tarihISO, kayit, adim: 'secim', baslangicSaat: kayit.baslangicSaat, bitisSaat: kayit.bitisSaat });
    } else {
      setModal({ dokKod, personelAdi, partTime, tarihISO, kayit: null, adim: 'duzenle', baslangicSaat: '', bitisSaat: '', secenekler: null });
    }
  };

  const modalKapat = () => setModal(null);

  // Tam zamanlı personel için "duzenle" adımına girildiğinde o günün sabit shift seçeneklerini getir.
  useEffect(() => {
    if (modal && modal.adim === 'duzenle' && modal.partTime === 0 && modal.secenekler == null) {
      getShiftSecenekleri(modal.tarihISO)
        .then(res => setModal(m => (m ? { ...m, secenekler: res.data } : m)))
        .catch(() => setModal(m => (m ? { ...m, secenekler: [] } : m)));
    }
  }, [modal]);

  const modalSil = async () => {
    setModal(m => ({ ...m, kaydediliyor: true }));
    try {
      await shiftGunSil(modal.dokKod, modal.tarihISO);
      setBasari('Shift kaydı silindi.');
      modalKapat();
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Silme işlemi başarısız.');
      modalKapat();
    }
  };

  const modalKaydet = async () => {
    if (!modal.baslangicSaat || !modal.bitisSaat) { setHata('Lütfen başlangıç ve bitiş saati giriniz.'); return; }
    setModal(m => ({ ...m, kaydediliyor: true }));
    try {
      await shiftGunDegistir({
        dokKod: modal.dokKod,
        tarih: modal.tarihISO,
        baslangicSaat: modal.baslangicSaat,
        bitisSaat: modal.bitisSaat,
      });
      setBasari('Shift kaydı kaydedildi.');
      modalKapat();
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'İşlem gerçekleştirilemedi.');
      setModal(m => ({ ...m, kaydediliyor: false }));
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 16, color: 'var(--med-dark)' }}>Shift Yönetimi</h2>

      {!modal && hata && <div className="hata-kutu">{hata}</div>}
      {!modal && basari && <div className="basari-kutu">{basari}</div>}

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 320 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Ay Seç
            </label>
            <input type="month" value={ayGirdi} onChange={e => setAyGirdi(e.target.value)} style={{ width: '100%' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Hafta Seç
            </label>
            <select value={seciliHaftaISO} onChange={e => setSeciliHaftaISO(e.target.value)} style={{ width: '100%' }}>
              {haftalar.map(h => {
                const hISO = toISODate(h);
                return (
                  <option key={hISO} value={hISO}>
                    {tarihTR(h)} – {tarihTR(new Date(h.getTime() + 6 * 86400000))}{haftaEtiket(hISO)}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Personel Seç
            </label>
            <select value={personelFiltre} onChange={e => setPersonelFiltre(e.target.value)} style={{ width: '100%' }}>
              <option value="">Tüm Personel</option>
              {personeller.map(p => <option key={p.kod} value={p.kod}>{p.ad}</option>)}
            </select>
          </div>
        </div>

        {olusturmaIzinli && seciliHaftaISO === buHaftaISO && (
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
            Bu haftanın geçmişte kalan günleri (bugünden önceki) otomatik atlanır, yalnızca bugün ve sonrası için kayıt oluşturulur.
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {olusturmaIzinli ? (
            <button onClick={topluOlustur} disabled={topluOlusturuluyor} className="btn btn-primary">
              {topluOlusturuluyor ? 'Oluşturuluyor...' : '⚡ Tüm Personel İçin Haftalık Shift Planı Oluştur'}
            </button>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Geçmiş bir hafta için toplu shift planı oluşturulamaz.
            </div>
          )}
          <button onClick={haftayiTemizle} disabled={temizleniyor} className="btn btn-danger">
            {temizleniyor ? 'Temizleniyor...' : '🗑️ Bu Haftanın Shift Planını Sil'}
          </button>
        </div>
      </div>

      <h3 style={{ marginBottom: 12, color: 'var(--med-dark)' }}>
        {tarihTR(hafta)} – {tarihTR(haftaBitis)} Haftasının Shift Kayıtları
      </h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
        Bir hücreye tıklayarak shift ekleyebilir, değiştirebilir veya silebilirsiniz.
      </p>
      <div className="card" style={{ padding: 0 }}>
        {yukleniyor ? <div className="loading">Yükleniyor...</div> : (
          gosterilenPersoneller.length === 0 ? <div className="empty">Personel bulunamadı.</div> : (
            <table className="veri-tablosu matris-tablosu">
              <colgroup>
                <col className="personel-sutun" />
                {gunler.map(g => <col key={toISODate(g)} />)}
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Personel</th>
                  {gunler.map(g => (
                    <th key={toISODate(g)} title={tarihTR(g)}>
                      {gunAdiKisa(g)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gosterilenPersoneller.map(p => (
                  <tr key={p.kod}>
                    <td className="personel-adi" title={p.ad}>{p.ad}</td>
                    {gunler.map(g => {
                      const gISO = toISODate(g);
                      const kayit = matris[p.kod]?.[gISO];
                      return (
                        <td key={gISO} style={{ cursor: 'pointer' }} onClick={() => hucreyeTikla(p.kod, p.ad, gISO, kayit)}>
                          {kayit ? (
                            <span className={`badge ${badgeSinifi(kayit)}`}>
                              {kisaEtiket(kayit)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--border)' }}>–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {modal && (
        <div
          onClick={modalKapat}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(13,43,94,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <div onClick={e => e.stopPropagation()} className="card" style={{ width: 360, background: '#fff' }}>
            <h3 style={{ marginTop: 0, marginBottom: 4, color: 'var(--med-dark)' }}>
              {modal.personelAdi}{modal.partTime !== 0 && <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}> (Part Time)</span>}
            </h3>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>{tarihTR(modal.tarihISO)}</div>

            {hata && <div className="hata-kutu" style={{ marginBottom: 12 }}>{hata}</div>}

            {modal.adim === 'secim' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  Mevcut shift: <span className={`badge ${badgeSinifi(modal.kayit)}`}>{uzunEtiket(modal.kayit)}</span>
                </div>
                <button
                  onClick={() => setModal(m => ({ ...m, adim: 'duzenle', secenekler: null }))}
                  className="btn btn-primary"
                  style={{ width: '100%', marginBottom: 8 }}
                >Değiştir</button>
                <button
                  onClick={modalSil}
                  disabled={modal.kaydediliyor}
                  className="btn btn-danger"
                  style={{ width: '100%', marginBottom: 8 }}
                >{modal.kaydediliyor ? 'Siliniyor...' : 'Sil'}</button>
                <button onClick={modalKapat} className="btn btn-secondary" style={{ width: '100%' }}>Vazgeç</button>
              </>
            )}

            {modal.adim === 'duzenle' && modal.partTime === 0 && (
              <>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Bu gün için geçerli shift saatlerinden birini seçin:
                </p>
                {modal.secenekler == null ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Yükleniyor...</div>
                ) : modal.secenekler.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Bu gün için tanımlı shift saati bulunamadı.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {modal.secenekler.map(s => {
                      const secili = modal.baslangicSaat === s.baslangicSaat && modal.bitisSaat === s.bitisSaat;
                      return (
                        <button
                          key={s.baslangicSaat}
                          type="button"
                          onClick={() => setModal(m => ({ ...m, baslangicSaat: s.baslangicSaat, bitisSaat: s.bitisSaat }))}
                          className={`btn ${secili ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ width: '100%' }}
                        >{s.etiket}</button>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={modalKaydet}
                  disabled={modal.kaydediliyor || !modal.baslangicSaat || !modal.bitisSaat}
                  className="btn btn-primary"
                  style={{ width: '100%', marginBottom: 8 }}
                >{modal.kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}</button>
                <button onClick={modalKapat} className="btn btn-secondary" style={{ width: '100%' }}>Vazgeç</button>
              </>
            )}

            {modal.adim === 'duzenle' && modal.partTime !== 0 && (() => {
              const sureDk = modal.baslangicSaat && modal.bitisSaat
                ? (Number(modal.bitisSaat.slice(0, 2)) * 60 + Number(modal.bitisSaat.slice(3, 5)))
                  - (Number(modal.baslangicSaat.slice(0, 2)) * 60 + Number(modal.baslangicSaat.slice(3, 5)))
                : null;
              const gecersizSure = sureDk != null && sureDk < 60;
              return (
                <>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Başlangıç</label>
                      <select value={modal.baslangicSaat} onChange={e => setModal(m => ({ ...m, baslangicSaat: e.target.value }))} style={{ width: '100%' }}>
                        <option value="">Seçin</option>
                        {SAAT_SECENEKLERI.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Bitiş</label>
                      <select value={modal.bitisSaat} onChange={e => setModal(m => ({ ...m, bitisSaat: e.target.value }))} style={{ width: '100%' }}>
                        <option value="">Seçin</option>
                        {SAAT_SECENEKLERI.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <p style={{ color: gecersizSure ? 'var(--danger, #c0392b)' : 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
                    Part time personel için serbest saat aralığı girilebilir, süre en az 1 saat olmalıdır.
                  </p>
                  <button
                    onClick={modalKaydet}
                    disabled={modal.kaydediliyor || !modal.baslangicSaat || !modal.bitisSaat || gecersizSure}
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: 8 }}
                  >{modal.kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}</button>
                  <button onClick={modalKapat} className="btn btn-secondary" style={{ width: '100%' }}>Vazgeç</button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
